import { useQuery } from '@tanstack/react-query';
import { APIIntegrationImPACT } from 'lib/api/firebase/apis/api-integration-impact';
import { useGetCurrentClinicianInfo } from 'lib/hooks/tanstack/use-getCurrentClinicianInfo';
import { useAppSelector } from 'lib/store/rootReducer';
import { queryKeyFactory } from 'lib/utils/queryKeyFactory';
import { useEffect, useMemo } from 'react';
import { PilotAlreadyJoinedState } from './display-pilot-already-joined';
import { PilotLoadingState } from './display-pilot-data-loading';
import { PilotJoinForm } from './form-join-pilot';
import { useErrorList } from 'lib/utils/useErrorList';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Flex } from 'antd';

export function useIsInPilot(id: string) {
  return useQuery({
    queryFn: () => APIIntegrationImPACT.isInPilot().then(_ => _.data),
    queryKey: queryKeyFactory.integration.impact.isInPilot(id)
  });
}

export type PilotPageState = 'loading' | 'in-pilot' | 'not-in-pilot';

interface StateMapArgs {
  isLoading: boolean;
  isInPilot: boolean;
}

const STATE_MAP: Array<{
  state: PilotPageState;
  condition: (arg: StateMapArgs) => boolean;
}> = [
  {
    state: 'loading',
    condition: _ => _.isLoading
  },
  {
    state: 'in-pilot',
    condition: _ => _.isInPilot
  },
  {
    state: 'not-in-pilot',
    condition: () => true
  }
];

function resolveState(args: StateMapArgs): PilotPageState {
  const state = STATE_MAP.find(_ => _.condition(args));
  if (!state) {
    throw new Error(
      `[resolveState] No matching states were found for the following arguments: ${JSON.stringify(
        args
      )}`
    );
  }
  return state.state;
}

export function PilotContent(props: { overrideState?: PilotPageState }) {
  const user = useAppSelector(_ => _.auth.user);
  const clinicianQuery = useGetCurrentClinicianInfo(user?.uid ?? '');
  const isInPilotQuery = useIsInPilot(user?.uid ?? '');
  const errorList = useErrorList();

  useEffect(
    () => {
      errorList.reset();
      if (clinicianQuery.error) {
        errorList.addError(resolveError(clinicianQuery.error));
      }

      if (isInPilotQuery.error) {
        errorList.addError(resolveError(isInPilotQuery.error));
      }
    },
    // eslint-disable-next-line
    [
      clinicianQuery.error,
      isInPilotQuery.error,
      errorList.addError,
      errorList.reset
    ]
  );

  const state = useMemo(
    () =>
      props.overrideState ??
      resolveState({
        isLoading: clinicianQuery.isLoading || isInPilotQuery.isLoading,
        isInPilot: isInPilotQuery.data?.isInPilot ?? false
      }),
    [
      clinicianQuery.isLoading,
      isInPilotQuery.isLoading,
      isInPilotQuery.data,
      props.overrideState
    ]
  );
  let Component;
  switch (state) {
    case 'loading':
      Component = <PilotLoadingState />;
      break;
    case 'in-pilot':
      Component = <PilotAlreadyJoinedState />;
      break;
    case 'not-in-pilot':
      Component = (
        <PilotJoinForm
          clinicianDisplayName={clinicianQuery.data?.displayName ?? ''}
        />
      );
      break;
    default:
      throw new Error(`Unknown sate (state='${state}')`);
  }

  return (
    <Flex vertical gap={16}>
      <ErrorListDisplay errorList={errorList} />
      {Component}
    </Flex>
  );
}
